import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { BottomNav } from "~/components/BottomNav";
import { ErrorModal } from "~/components/ErrorModal";
import {
  MIN_POINT_FOR_VIDEO_CALL,
  MIN_POINT_FOR_VOICE_CALL,
} from "~/constants/actionsPoint";
import { CALL_TYPE } from "~/constants/call";
import { TEXT } from "~/constants/text";
import { CallEndedModal } from "~/features/call/components/CallEndedModal";
import { CallOverlay } from "~/features/call/components/CallOverlay";
import { useCallCleanup } from "~/features/call/hooks/useCallCleanup";
import { DemoCallOverlay } from "~/features/demoCall/components/DemoCallOverlay";
import { NewsModal } from "~/features/news/components/NewsModal";
import { InsufficientPointSheet } from "~/features/purchase/components/InsufficientPointSheet";
import { PurchaseQuickChargeSheet } from "~/features/purchase/components/PurchaseQuickChargeSheet";
import { useNotificationPolling } from "~/features/settings/notifications/hooks/useNotificationPolling";
import { SignupBonusModal } from "~/features/signupBonus/SignupBonusModal";
import { VideoCallPromoModal } from "~/features/videoCallPromo/VideoCallPromoModal";
import { useDelayedUnmount } from "~/hooks/useDelayedUnmount";
import { usePrefetchGiftImages } from "~/hooks/usePrefetchGiftImages";
import { useSendCallRequest } from "~/hooks/useSendCallRequest";
import { getAvatarUrl } from "~/lib/imageUrl";
import { useAuthStore } from "~/stores/useAuthStore";
import { useCallStore } from "~/stores/useCallStore";
import { useInsufficientPointSheetStore } from "~/stores/useInsufficientPointSheetStore";
import { useNewsModalStore } from "~/stores/useNewsModalStore";
import { useQuickChargeStore } from "~/stores/useQuickChargeStore";
import { useSignupBonusStore } from "~/stores/useSignupBonusStore";
import { shouldShowLaunchCampaignIntro } from "~/utils/launchCampaignIntro";

const AuthenticatedLayout = () => {
  useNotificationPolling();
  useCallCleanup();
  // 通話/ライブ後レビューのギフト画像を起動後アイドル時に先読みし、初回表示のもたつきを防ぐ
  usePrefetchGiftImages();
  const { location } = useRouterState();
  const navigate = useNavigate();
  const phase = useCallStore((s) => s.phase);
  const partnerName = useCallStore((s) => s.partnerName);
  const callType = useCallStore((s) => s.callType);
  // ストアにはアバター ID が入るため、表示用に URL へ変換する
  const partnerAvatarId = useCallStore((s) => s.partnerAvatarUrl);
  const partnerAvatarUrl = getAvatarUrl(partnerAvatarId);
  const partnerId = useCallStore((s) => s.partnerId);
  const { sendCallRequest, isPending: isSendingCallRequest } =
    useSendCallRequest();
  const newsModalOpen = useNewsModalStore((s) => s.isOpen);
  const signupBonusOpen = useSignupBonusStore((s) => s.isOpen);
  const quickChargeOpen = useQuickChargeStore((s) => s.open);
  const quickChargeOnSuccess = useQuickChargeStore((s) => s.onChargeSuccessCb);
  const hideQuickCharge = useQuickChargeStore((s) => s.hide);
  const insufficientSheetOpen = useInsufficientPointSheetStore((s) => s.open);
  const insufficientSheetNeeded = useInsufficientPointSheetStore(
    (s) => s.needed,
  );
  const insufficientSheetOnClose = useInsufficientPointSheetStore(
    (s) => s.onCloseCb,
  );
  const insufficientSheetOnSuccess = useInsufficientPointSheetStore(
    (s) => s.onChargeSuccessCb,
  );
  const insufficientSheetShakeSignal = useInsufficientPointSheetStore(
    (s) => s.shakeSignal,
  );
  const hideInsufficientSheet = useInsufficientPointSheetStore((s) => s.hide);
  // 閉じても slide-down 退場アニメのぶんだけマウントを維持する。
  const quickChargeRender = useDelayedUnmount(quickChargeOpen);
  const insufficientSheetRender = useDelayedUnmount(insufficientSheetOpen);

  useEffect(() => {
    const handler = (e: Event) => {
      const { userid } = (e as CustomEvent<{ userid?: string }>).detail;
      if (!userid) return;
      navigate({
        to: "/messages/$conversationId",
        params: { conversationId: userid },
      });
    };
    window.addEventListener("pushTapped", handler);
    return () => window.removeEventListener("pushTapped", handler);
  }, [navigate]);

  useEffect(() => {
    // location.pathname はミュータブル値のため deps に含めず、effect 本体内で参照する
    const pathname = location.pathname;
    if (
      phase.type === "ended" &&
      !pathname.startsWith("/call") &&
      phase.reason === "pointShortage"
    ) {
      useInsufficientPointSheetStore.getState().show({
        needed: {
          label: TEXT.insufficientPointSheet.neededLabelCall,
          value:
            callType === "voice"
              ? MIN_POINT_FOR_VOICE_CALL
              : MIN_POINT_FOR_VIDEO_CALL,
          unit: TEXT.insufficientPointSheet.unitPerMin,
        },
        onClose: () => useCallStore.getState().reset(),
      });
    }
  }, [phase, location, callType]);

  const BOTTOM_NAV_VISIBLE_PATHS = [
    "/discover",
    "/live",
    "/board",
    "/messages",
    "/my-page",
  ];

  const showBottomNav = BOTTOM_NAV_VISIBLE_PATHS.some(
    (p) => location.pathname === p || location.pathname === `${p}/`,
  );

  const isCallEnded =
    phase.type === "ended" && !location.pathname.startsWith("/call");

  const handleCloseEndedModal = () => {
    useCallStore.getState().reset();
  };

  const handleFavorite = () => {
    // TODO: お気に入り API 実装後に接続
  };

  const handleCallRequest = async () => {
    if (isSendingCallRequest) return;
    const id = partnerId ?? "";
    if (!id) return;
    const ok = await sendCallRequest({
      partnerId: id,
      partnerName: partnerName ?? "",
    });
    if (ok) useCallStore.getState().reset();
  };

  const handleVoiceCallRequest = async () => {
    if (isSendingCallRequest) return;
    const id = partnerId ?? "";
    if (!id) return;
    const ok = await sendCallRequest({
      partnerId: id,
      partnerName: partnerName ?? "",
      callType: CALL_TYPE.VOICE,
    });
    if (ok) useCallStore.getState().reset();
  };

  const handleSendMessage = () => {
    const id = partnerId ?? "";
    useCallStore.getState().reset();
    navigate({
      to: "/messages/$conversationId",
      params: { conversationId: id },
    });
  };

  return (
    <div>
      <Outlet />
      {showBottomNav && <BottomNav />}
      <CallOverlay />
      <DemoCallOverlay />
      <ErrorModal />
      {/* 実ビデオ通話訴求モーダル（デモ通話終了時に store 経由で開く）。
          Dialog が open で自己制御するため常時マウントする。 */}
      <VideoCallPromoModal />
      {isCallEnded &&
        phase.type === "ended" &&
        phase.reason !== "pointShortage" && (
          <CallEndedModal
            reason={phase.reason}
            partnerName={partnerName}
            partnerAvatarUrl={partnerAvatarUrl}
            onClose={handleCloseEndedModal}
            onFavorite={handleFavorite}
            onVideoCall={handleCallRequest}
            onVoiceCall={handleVoiceCallRequest}
            onSendMessage={handleSendMessage}
          />
        )}
      {/* ニュースモーダル（settings / discover から store 経由で開く）。
          開いている間だけマウントする。 */}
      {newsModalOpen && <NewsModal />}
      {/* 新規登録ボーナス（250pt）付与アニメーション。開いている間だけマウントする。 */}
      {signupBonusOpen && <SignupBonusModal />}
      {/* ポイントクイックチャージシート（call / live から store 経由で開く）。
          常時マウントだと useUserPoint が走り続けるため、開いている間だけマウントする。
          open={false} でも退場アニメ中は描画し続ける（useDelayedUnmount）。 */}
      {quickChargeRender && (
        <PurchaseQuickChargeSheet
          open={quickChargeOpen}
          onClose={hideQuickCharge}
          onChargeSuccess={(points) => quickChargeOnSuccess?.(points)}
        />
      )}
      {/* ポイント不足シート（IAP その場チャージ）。ライブ入室・メッセージ送信など
          複数 feature から useInsufficientPointSheetStore 経由で開く。
          開いている間だけマウントして useUserPoint / useStartPurchase が常時起動しないようにする。
          open={false} でも退場アニメ中は描画し続ける（useDelayedUnmount）。 */}
      {insufficientSheetRender && insufficientSheetNeeded && (
        <InsufficientPointSheet
          open={insufficientSheetOpen}
          needed={insufficientSheetNeeded}
          shakeSignal={insufficientSheetShakeSignal}
          onClose={() => {
            insufficientSheetOnClose?.();
            hideInsufficientSheet();
          }}
          onChargeSuccess={(points) => {
            insufficientSheetOnSuccess?.(points);
            hideInsufficientSheet();
          }}
        />
      )}
    </div>
  );
};

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: ({ location }) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      throw redirect({ to: "/login" });
    }
    // アプリ起動時キャンペーン動画: このセッションでまだ未表示なら、
    // discover 等への遷移前に /launch-campaign を挟む。
    // 自分自身への遷移まで横取りすると無限リダイレクトになるため除外する。
    if (
      location.pathname !== "/launch-campaign" &&
      shouldShowLaunchCampaignIntro()
    ) {
      throw redirect({ to: "/launch-campaign" });
    }
  },
  component: AuthenticatedLayout,
});
